import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { CANAIS_FLUXO } from '@malgoos/shared';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Segmentado, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { post } from '../../nucleo/api';
import { numero, relativo } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { BASE_FLUXOS, chaveFluxos, COR_ESTADO_FLUXO, type EstadoFluxo, type FluxoResumo } from './tipos-fluxos';

type Filtro = 'todas' | 'ativo' | 'pausado' | 'rascunho' | 'arquivado';

/**
 * U14 F1: Automações > Automações. A lista das automações de conversa (fluxos do Instagram e do WhatsApp, estilo ManyChat)
 * e "Nova automação", que cria o rascunho com o gatilho e abre o editor em canvas.
 */
export function Fluxos() {
  const { t } = useTranslation('automacoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [novo, setNovo] = useState(false);
  const podeEditar = s.pode('automacoes.editar');
  const colunas: Coluna<FluxoResumo>[] = [
    { chave: 'nome', rotulo: t('fluxos.colunas.nome'), principal: true },
    { chave: 'canal', rotulo: t('fluxos.colunas.canal'), render: (f) => t(`fluxos.canais.${f.canal}`) },
    { chave: 'estado', rotulo: t('fluxos.colunas.estado'), render: (f) => <Selo cor={COR_ESTADO_FLUXO[f.estado]}>{t(`fluxos.estados.${f.estado}`)}</Selo> },
    { chave: 'versao_numero', rotulo: t('fluxos.colunas.versao'), render: (f) => (f.versao_numero ? t('fluxos.versao', { n: f.versao_numero }) : t('fluxos.semVersao')) },
    { chave: 'ativas', rotulo: t('fluxos.colunas.ativas'), alinhar: 'direita', render: (f) => numero(f.ativas) },
    { chave: 'execucoes_7d', rotulo: t('fluxos.colunas.semana'), alinhar: 'direita', render: (f) => numero(f.execucoes_7d) },
    { chave: 'updated_at', rotulo: t('fluxos.colunas.atualizada'), render: (f) => relativo(f.updated_at) },
  ];
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('fluxos.nome'), tipo: 'texto', obrigatorio: true, max: 120 },
    // U14 F2: palavra-chave (Direct ou WhatsApp) ou comentário em post (Instagram).
    { nome: 'gatilho', rotulo: t('fluxos.gatilho'), tipo: 'selecao', obrigatorio: true, opcoes: (['palavra', 'comentario', 'app', 'broadcast'] as const).map((g) => ({ valor: g, rotulo: t(`fluxos.gatilhos.${g}`) })) },
    { nome: 'canal', rotulo: t('fluxos.canal'), tipo: 'selecao', obrigatorio: true, opcoes: CANAIS_FLUXO.map((c) => ({ valor: c, rotulo: t(`fluxos.canais.${c}`) })), visivel: (v) => v.gatilho === 'palavra' },
  ];
  const opcoesFiltro: { valor: Filtro; rotulo: string }[] = [
    { valor: 'todas', rotulo: t('fluxos.filtros.todas') },
    { valor: 'ativo', rotulo: t('fluxos.filtros.ativas') },
    { valor: 'pausado', rotulo: t('fluxos.filtros.pausadas') },
    { valor: 'rascunho', rotulo: t('fluxos.filtros.rascunhos') },
    { valor: 'arquivado', rotulo: t('fluxos.filtros.arquivadas') },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-4" data-fluxos-automacoes>
      {podeEditar && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('fluxos.novo')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<FluxoResumo>
        tela="automacoes.fluxos"
        caminho="/automacoes/fluxos"
        params={filtro === 'todas' ? {} : { estado: filtro as EstadoFluxo }}
        chaveConsulta={[...chaveFluxos, 'lista', filtro]}
        colunas={colunas}
        filtros={{ filtro }}
        aoAplicarFiltros={(x) => setFiltro(opcoesFiltro.some((o) => o.valor === x.filtro) ? (x.filtro as Filtro) : 'todas')}
        filtrosExtra={<Segmentado<Filtro> rotulo={t('fluxos.colunas.estado')} valor={filtro} opcoes={opcoesFiltro} aoMudar={setFiltro} />}
        aoClicar={(f) => navegar(`${BASE_FLUXOS}/fluxo/${f.id}`)}
        vazio={<Vazio texto={podeEditar ? t('fluxos.vazio') : t('fluxos.vazioLeitura')} />}
        buscaNaUrl
      />
      {novo && (
        <Drawer
          aberto
          aoFechar={() => setNovo(false)}
          titulo={t('fluxos.novo')}
          rodape={
            <>
              <Botao variante="fantasma" onClick={() => setNovo(false)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" type="submit" form="form-fluxo-novo">
                {tn('comum.salvar')}
              </Botao>
            </>
          }
        >
          <Formulario
            id="form-fluxo-novo"
            campos={campos}
            inicial={{ canal: 'qualquer', gatilho: 'palavra' }}
            aoEnviar={async (v) => {
              const id = crypto.randomUUID();
              // U14 F6: evento de app externo roda sem canal; U14 F5: o gatilho de broadcast é do WhatsApp (e do grupo: o servidor grava sem empresa).
              const gatilho = v.gatilho === 'comentario' || v.gatilho === 'app' || v.gatilho === 'broadcast' ? v.gatilho : 'palavra';
              await post('/automacoes/fluxos', { id, nome: v.nome, gatilho, canal: gatilho === 'comentario' ? 'instagram' : gatilho === 'app' ? 'qualquer' : gatilho === 'broadcast' ? 'whatsapp' : v.canal });
              await qc.invalidateQueries({ queryKey: chaveFluxos });
              toast.sucesso(t('fluxos.criada'));
              setNovo(false);
              navegar(`${BASE_FLUXOS}/fluxo/${id}`);
            }}
          />
        </Drawer>
      )}
    </div>
  );
}
